import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const getDisplayOptions = (translationService: TranslationService) => [
  { value: 'R', viewValue: translationService.translate('requiredText') },
  { value: 'Y', viewValue: translationService.translate('yesAvailableText') },
  { value: 'N', viewValue: translationService.translate('notAvailableText') }
];

export function getProcessCodesFromDealerSetup(data: any, translationService: TranslationService) {
  return [
    { code: 'AD', description: translationService.translate('adjustmentText'), checked: data?.btmAdAvailable === 'Y' },
    { code: 'BS', description: translationService.translate('buildForStockText'), checked: data?.btmBsAvailable === 'Y' },
    { code: 'CC', description: translationService.translate('casingCreditText'), checked: data?.btmCcAvailable === 'Y' },
    { code: 'CP', description: translationService.translate('casingPurchaseText'), checked: data?.btmCpAvailable === 'Y' },
    { code: 'DS', description: translationService.translate('dealerStockText'), checked: data?.btmDsAvailable === 'Y' },
    { code: 'IO', description: translationService.translate('inspectOnlyText'), checked: data?.btmIoAvailable === 'Y' },
    { code: 'NP', description: translationService.translate('noPickupText'), checked: data?.btmNpAvailable === 'Y' },
    { code: 'RO', description: translationService.translate('repairOnlyText'), checked: data?.btmRoAvailable === 'Y' },
    { code: 'RR', description: translationService.translate('repairAndRetreadText'), checked: data?.btmRrAvailable === 'Y' },
    { code: 'RS', description: translationService.translate('reserveAndStockText'), checked: data?.btmRsAvailable === 'Y' },
    { code: 'SO', description: translationService.translate('serviceOnlyText'), checked: data?.btmSoAvailable === 'Y' },
    { code: 'SC', description: translationService.translate('scrapText'), checked: data?.btmScAvailable === 'Y' }
  ];
}

export function sqlInjectionValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) return null;
    // Basic SQL injection patterns
    const sqlPatterns = [
      /\b(select|insert|update|delete|drop|alter|create|truncate|exec|union|--|;|\bor\b|\band\b)\b/i,
      /['"].*?['"]/,
      /\bwhere\b/i,
      /\bfrom\b/i,
      /\btable\b/i,
      /\binto\b/i,
      /\bvalues\b/i
    ];
    for (const pattern of sqlPatterns) {
      if (pattern.test(value)) {
        return { sqlInjection: true };
      }
    }
    return null;
  };
}

export function enforceThreeDigits(event: any, formGroup?: any): void {
  const input = event.target as HTMLInputElement;
  // Remove negative sign, leading zeros, and limit to 3 digits
  input.value = input.value
    .replace('-', '')
    .replace(/^0+/, '')
    .replace(/[^0-9]/g, '')
    .slice(0, 3);

  let num = Number(input.value);
  if (isNaN(num) || num < 1) {
    input.value = '';
  } else if (num > 999) {
    input.value = '999';
  }

  if (formGroup && input.getAttribute('formControlName')) {
    formGroup.get(input.getAttribute('formControlName'))?.setValue(input.value);
  }
}

// Map processCodes to API fields
export const processCodeToFieldMap = {
  AD: 'btmAdAvailable',
  BS: 'btmBsAvailable',
  CC: 'btmCcAvailable',
  CP: 'btmCpAvailable',
  DS: 'btmDsAvailable',
  IO: 'btmIoAvailable',
  NP: 'btmNpAvailable',
  RO: 'btmRoAvailable',
  RR: 'btmRrAvailable',
  SO: 'btmSoAvailable',
  SC: 'btmScAvailable',
  RS: 'btmRsAvailable'
};

export const booleanDealerSetupFields = [
  'posEnable',
  'barCodeLeadingZero',
  'overrideGradeEditable',
  'mfgDotReqOnWo',
  'displayDeleteCasing',
  'markWoAvailable',
  'autoShipListNumbering',
  'btmAllowBulkEntry',
  'btmAllowPickupChange',
  'btmAllowModifyDueDate',
  'btmAutoGenerateWo',
  'btmAutoGeneratePlNum',
  'btmAllowCreateWo'
];

export function buildProcessCodeFields(processCodes: any[]): Record<string, string> {
  const fields: Record<string, string> = {};
  processCodes.forEach((codeObj) => {
    const field = processCodeToFieldMap[codeObj.code];
    if (field) {
      fields[field] = codeObj.checked ? 'Y' : 'N';
    }
  });
  return fields;
}

export function mapYNFieldsToBooleans(data: any, fields: string[]): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  fields.forEach((field) => {
    result[field] = data?.[field] === 'Y';
  });
  return result;
}

export function mapBooleansToYN(obj: Record<string, any>, fields: string[]): Record<string, any> {
  const result = { ...obj };
  fields.forEach((field) => {
    if (typeof result[field] === 'boolean') {
      result[field] = result[field] ? 'Y' : 'N';
    }
  });
  return result;
}
